import { useState, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';
import axios from 'axios';
import { TrendUp, TrendDown, Gear, X, Eye, EyeSlash, ArrowsOutLineHorizontal, ArrowsInLineHorizontal, DotsSixVertical, ChartLine } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Evento personalizado para abrir ventanas desde el ticker
export const openTickerChart = (ticker, name) => {
  const event = new CustomEvent('openTickerChart', { 
    detail: { ticker, name } 
  });
  window.dispatchEvent(event);
};

export const StockTickerHorizontal = () => {
  const [topPerformers, setTopPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topLimit, setTopLimit] = useState(() => {
    const saved = localStorage.getItem('tickerTopLimit');
    return saved ? parseInt(saved) : 3;
  });
  const [isVisible, setIsVisible] = useState(() => {
    const saved = localStorage.getItem('tickerHVisible');
    // Oculta por defecto: se activa desde el "Gestor" (petición usuario)
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('tickerHExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showSettings, setShowSettings] = useState(false);
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem('tickerHPosition');
      // Default position: start after sidebar (224px width + 16px margin)
      return saved ? JSON.parse(saved) : { x: 240, y: 0 };
    } catch (e) {
      return { x: 240, y: 0 };
    }
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  
  const tickerRef = useRef(null);
  const scrollRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);
  const scrollPosition = useRef(0);

  // Fetch top performers por categoría
  const fetchTopPerformers = async () => {
    setLoading(true);
    try {
      // Obtener las mejores de cada categoría
      const response = await axios.get(`${API}/market/top-by-category?per_category=${topLimit}`);
      if (response.data && response.data.performers) {
        // Ya vienen ordenadas por categoría, las ordenamos por rendimiento
        const sorted = [...response.data.performers].sort((a, b) => 
          (b.change_pct || 0) - (a.change_pct || 0)
        );
        setTopPerformers(sorted);
      }
    } catch (error) {
      logger.error('Error fetching top performers:', error);
      // Fallback al endpoint anterior
      try {
        const fallback = await axios.get(`${API}/market/top-performers?limit=${topLimit * 4}`);
        if (fallback.data && fallback.data.performers) {
          const sorted = [...fallback.data.performers].sort((a, b) => 
            (b.change_pct || 0) - (a.change_pct || 0)
          );
          setTopPerformers(sorted);
        }
      } catch (e) {
        logger.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  // Detectar visibilidad de la pestaña
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    fetchTopPerformers();
    
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchTopPerformers();
      }
    }, 300000); // 5 minutos
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLimit]);

  // Refrescar al volver a la pestaña
  useEffect(() => {
    if (isTabVisible) {
      fetchTopPerformers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabVisible]);

  // Auto-scroll animation
  useEffect(() => {
    if (!scrollRef.current || loading || topPerformers.length === 0) return;

    const scroll = scrollRef.current;
    const scrollWidth = scroll.scrollWidth;
    const clientWidth = scroll.clientWidth;
    
    if (scrollWidth <= clientWidth) return;

    const animate = () => {
      // Velocidad del ticker: 1.5 px/frame (~3x más rápido que antes 0.5)
      scrollPosition.current += 1.5;
      if (scrollPosition.current >= scrollWidth / 2) {
        scrollPosition.current = 0;
      }
      scroll.scrollLeft = scrollPosition.current;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [loading, topPerformers]);

  // Guardar preferencias
  useEffect(() => {
    localStorage.setItem('tickerHVisible', JSON.stringify(isVisible));
  }, [isVisible]);

  // Control desde el "Gestor" (evento global): mostrar/ocultar la barra flotante
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && typeof e.detail.visible === 'boolean') setIsVisible(e.detail.visible);
      else setIsVisible(v => !v);
    };
    window.addEventListener('kapital-toggle-ticker', handler);
    return () => window.removeEventListener('kapital-toggle-ticker', handler);
  }, []);

  useEffect(() => {
    localStorage.setItem('tickerHExpanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  useEffect(() => {
    localStorage.setItem('tickerTopLimit', topLimit.toString());
  }, [topLimit]);

  useEffect(() => {
    localStorage.setItem('tickerHPosition', JSON.stringify(position));
  }, [position]);

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('button') || e.target.closest('select')) return;
    
    setIsDragging(true);
    const rect = tickerRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    
    // Limitar dentro de la pantalla
    const maxX = window.innerWidth - (tickerRef.current?.offsetWidth || 300);
    const maxY = window.innerHeight - 48;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const formatPrice = (price) => {
    if (!price) return '0';
    if (price >= 1000) return price.toFixed(0);
    if (price >= 100) return price.toFixed(1);
    return price.toFixed(2);
  };

  // Duplicar items para scroll infinito
  const displayItems = [...topPerformers, ...topPerformers];

  // Botón flotante para mostrar cuando está oculto - Posición ajustada para no taparse con la barra
  // Oculta por defecto: el control de visibilidad vive ahora en el "Gestor"
  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Barra del Ticker Horizontal - Draggable */}
      <div 
        ref={tickerRef}
        className={`fixed z-30 bg-zinc-900/95 backdrop-blur-sm border border-zinc-800 rounded-lg shadow-2xl transition-all duration-300 ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        style={{ 
          top: position.y,
          left: window.innerWidth < 640 ? 8 : position.x,
          width: window.innerWidth < 640 ? 'calc(100% - 16px)' : (isExpanded ? 'calc(100% - 32px)' : 'auto'),
          maxWidth: window.innerWidth < 640 ? 'calc(100% - 16px)' : (isExpanded ? 'none' : '800px'),
          margin: '0'
        }}
        onMouseDown={handleMouseDown}
        data-testid="horizontal-ticker"
      >
        <div className="flex items-center h-10 sm:h-11 w-full overflow-hidden">
          {/* Handle de arrastre - oculto en móvil */}
          <div className="hidden sm:flex items-center gap-1 px-1 sm:px-2 border-r border-zinc-800 h-full cursor-grab">
            <span className="flex items-center justify-center p-1 rounded border border-gold-500/50">
              <DotsSixVertical size={16} className="text-gold-400" />
            </span>
          </div>

          {/* Controles - más compactos en móvil */}
          <div className="flex items-center gap-0.5 px-1 sm:px-2 border-r border-zinc-800 h-full flex-shrink-0">
            <button
              onClick={() => setIsVisible(false)}
              className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors border border-gold-500/50"
              title="Ocultar Ticker"
            >
              <EyeSlash size={12} className="sm:w-3.5 sm:h-3.5" />
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`p-1 hover:bg-zinc-800 rounded transition-colors hidden sm:block border border-gold-500/50 ${isExpanded ? 'text-green-400' : 'text-zinc-400 hover:text-white'}`}
              title={isExpanded ? "Reducir" : "Expandir"}
            >
              {isExpanded ? <ArrowsInLineHorizontal size={14} /> : <ArrowsOutLineHorizontal size={14} />}
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-1 hover:bg-zinc-800 rounded transition-colors hidden sm:block border border-gold-500/50 ${showSettings ? 'text-zinc-400' : 'text-zinc-400 hover:text-white'}`}
              title="Configurar"
            >
              <Gear size={14} />
            </button>
          </div>

          {/* Selector de límite por categoría - Oculto en móvil */}
          <div className="hidden sm:flex items-center gap-1 px-2 border-r border-zinc-800 h-full">
            <span className="text-[10px] text-zinc-500">TOP</span>
            <select
              value={topLimit}
              onChange={(e) => setTopLimit(parseInt(e.target.value))}
              className="bg-zinc-900 text-xs text-white font-bold cursor-pointer focus:outline-none rounded px-1 [&>option]:bg-zinc-900 [&>option]:text-white"
            >
              <option value={3} className="bg-zinc-900 text-white">3</option>
              <option value={5} className="bg-zinc-900 text-white">5</option>
              <option value={10} className="bg-zinc-900 text-white">10</option>
            </select>
            <span className="text-[10px] text-zinc-500">/cat</span>
          </div>

          {/* Stocks con auto-scroll */}
          <div 
            ref={scrollRef}
            className="flex-1 overflow-hidden"
            onMouseEnter={() => {
              if (animationRef.current) cancelAnimationFrame(animationRef.current);
            }}
            onMouseLeave={() => {
              if (scrollRef.current && topPerformers.length > 0) {
                const animate = () => {
                  scrollPosition.current += 1.5;
                  const scrollWidth = scrollRef.current?.scrollWidth || 0;
                  if (scrollPosition.current >= scrollWidth / 2) {
                    scrollPosition.current = 0;
                  }
                  if (scrollRef.current) {
                    scrollRef.current.scrollLeft = scrollPosition.current;
                  }
                  animationRef.current = requestAnimationFrame(animate);
                };
                animationRef.current = requestAnimationFrame(animate);
              }
            }}
          >
            <div className="flex items-center gap-0 whitespace-nowrap">
              {loading ? (
                <div className="flex items-center gap-4 px-4">
                  {[1,2,3,4,5,6,7,8].map(i => (
                    <div key={i} className="animate-pulse flex items-center gap-2">
                      <div className="h-3 w-10 bg-zinc-800 rounded"></div>
                      <div className="h-3 w-14 bg-zinc-800 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : (
                displayItems.map((stock, index) => {
                  // Color por región
                  const regionColors = {
                    'España': 'text-red-400',
                    'Europa': 'text-zinc-400',
                    'USA': 'text-green-400',
                    'Asia': 'text-yellow-400'
                  };
                  const regionColor = regionColors[stock.region] || 'text-zinc-400';
                  const regionLetter = stock.region?.charAt(0) || '?';
                  
                  return (
                    <div
                      key={`${stock.ticker}-${index}`}
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 border-r border-zinc-800/50 hover:bg-zinc-800/50 transition-colors cursor-pointer group flex-shrink-0"
                      data-testid={`ticker-item-${stock.ticker}`}
                      title={`Click para ver gráfico de ${stock.ticker}`}
                      onClick={() => openTickerChart(stock.ticker, stock.name)}
                    >
                      <span className={`text-[9px] sm:text-[10px] px-1 rounded ${regionColor} bg-zinc-800/50 font-bold`}>
                        {regionLetter}
                      </span>
                      <span className="text-[11px] sm:text-[13px] font-bold text-white group-hover:text-gold-400 transition-colors whitespace-nowrap">{stock.name || stock.ticker}</span>
                      <span className="text-[10px] sm:text-[12px] text-zinc-400 whitespace-nowrap">
                        {formatPrice(stock.price_eur)}€
                      </span>
                      <span className={`text-[10px] sm:text-[12px] font-semibold flex items-center gap-0.5 whitespace-nowrap ${
                        (stock.change_pct || 0) >= 0 ? 'text-[#00ff6a]' : 'text-[#ff0000]'
                      }`}>
                        {(stock.change_pct || 0) >= 0 ? <TrendUp size={10} /> : <TrendDown size={10} />}
                        {(stock.change_pct || 0) >= 0 ? '+' : ''}{(stock.change_pct || 0).toFixed(2)}%
                      </span>
                      <ChartLine size={10} className="text-zinc-600 group-hover:text-gold-400 transition-colors hidden sm:block" />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Panel de info cuando se abre settings */}
        {showSettings && (
          <div className="border-t border-zinc-800 bg-zinc-900/95 p-3 text-xs text-zinc-400">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="text-zinc-500">Top {topLimit} por región:</span>
                <span className="text-red-400 font-bold">E=España</span>
                <span className="text-zinc-400 font-bold">E=Europa</span>
                <span className="text-green-400 font-bold">U=USA</span>
                <span className="text-yellow-400 font-bold">A=Asia</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPosition({ x: 0, y: 0 })}
                  className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300"
                >
                  Resetear posición
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Spacer para el contenido - solo si está arriba */}
      {position.y < 100 && <div className="h-14"></div>}
    </>
  );
};

export default StockTickerHorizontal;
